import * as React from 'react';
import { FC } from 'react';
import moment from 'moment';
import { Alert, Form, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, MenuItem, TextField } from '@mui/material';
import HelpIcon from '@material-ui/icons/Help';
import { useDispatch, useSelector } from 'react-redux';
import StyledTable, { PrimaryText } from '../../../components/StyledTable';
import { SettingsItemContentProps } from '../SettingsItem';
import { CheckInputField, TextInputField } from '../../../components/Forms/fields';
import { getLanguage, useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import {
    IntegrationsSettingsData,
    IntegrationMailerLiteSettings,
    IntegrationType,
    MailerLiteAvailableFieldData,
    MailerLiteFieldMappingData,
} from '../../../models';
import { alertError, alertSuccess } from '../../../store/alerts/actions';
import { getErrorMessage } from '../../../ajax/errors';
import { getMailerLiteFieldsAsync, syncIntegrationAsync, testMailerLiteApiKeyAsync } from '../../../ajax/requests';
import { loadSettings } from '../../../store/settings/actions';
import { getSettings } from '../../../store/settings/selectors';
import {
    ActionsRow,
    FieldKeyText,
    HintText,
    ImportCard,
    ImportHeader,
    ImportProgressRow,
    ImportTitle,
    Layout,
    MappingActions,
    MappingHeader,
    MappingTableBody,
    MutedText,
    StatusText,
    StepCard,
    StepCards,
    StepNumber,
    StepTitle,
} from './MailerLiteSettings.styles';
import AppButton from '../../../components/AppButton';

export interface IntegrationsSettingsProps extends SettingsItemContentProps {
    values: IntegrationsSettingsData,
    onChange: (settings: IntegrationsSettingsData, save?: boolean) => void,
}

interface AppointmintFieldOption {
    key: string;
    labelKey: TK;
}

type MappingMode = 'none' | 'field' | 'tagPrefix';

const DEFAULT_SETTINGS: IntegrationMailerLiteSettings = {
    apiKey: '',
    fieldMappings: [],
};

const APPOINTMINT_FIELD_OPTIONS: AppointmintFieldOption[] = [
    { key: 'name', labelKey: TK.mailerLiteAppointmintFieldName },
    { key: 'email', labelKey: TK.mailerLiteAppointmintFieldEmail },
    { key: 'phoneNumbers', labelKey: TK.mailerLiteAppointmintFieldPhoneNumbers },
    { key: 'birthday', labelKey: TK.mailerLiteAppointmintFieldBirthday },
    { key: 'sex', labelKey: TK.mailerLiteAppointmintFieldSex },
    { key: 'job', labelKey: TK.mailerLiteAppointmintFieldJob },
    { key: 'origin', labelKey: TK.mailerLiteAppointmintFieldOrigin },
    { key: 'taxNumber', labelKey: TK.mailerLiteAppointmintFieldTaxNumber },
    { key: 'healthNumber', labelKey: TK.mailerLiteAppointmintFieldHealthNumber },
    { key: 'subsystemName', labelKey: TK.mailerLiteAppointmintFieldSubsystemName },
    { key: 'subsystemNumber', labelKey: TK.mailerLiteAppointmintFieldSubsystemNumber },
    { key: 'addressLine1', labelKey: TK.mailerLiteAppointmintFieldAddressLine1 },
    { key: 'addressLine2', labelKey: TK.mailerLiteAppointmintFieldAddressLine2 },
    { key: 'zipCode', labelKey: TK.mailerLiteAppointmintFieldZipCode },
    { key: 'city', labelKey: TK.mailerLiteAppointmintFieldCity },
    { key: 'notes', labelKey: TK.mailerLiteAppointmintFieldNotes },
];

const mergeFieldMappings = (
    fields: MailerLiteAvailableFieldData[],
    currentMappings?: MailerLiteFieldMappingData[],
): MailerLiteFieldMappingData[] => {
    const currentMappingsByKey = new Map(
        (currentMappings ?? []).map(mapping => [mapping.mailerLiteFieldKey, mapping]),
    );

    return fields.map(field => ({
        mailerLiteFieldKey: field.key,
        mailerLiteFieldName: field.name,
        appointmintFieldKey: currentMappingsByKey.get(field.key)?.appointmintFieldKey,
        clientTagPrefix: currentMappingsByKey.get(field.key)?.clientTagPrefix,
    }));
};

const getMappingMode = (mapping: MailerLiteFieldMappingData): MappingMode => {
    if (mapping.clientTagPrefix) {
        return 'tagPrefix';
    }

    if (mapping.appointmintFieldKey) {
        return 'field';
    }

    return 'none';
};

const hasDuplicateFieldMappings = (fieldMappings?: MailerLiteFieldMappingData[]) => (
    fieldMappings ?? []
)
    .filter(mapping => getMappingMode(mapping) === 'field')
    .map(mapping => mapping.appointmintFieldKey)
    .filter((value): value is string => !!value)
    .some((value, index, array) => array.indexOf(value) !== index);

const getInitialStep = (settings: IntegrationMailerLiteSettings) => settings.fieldMappings?.length ? 2 : 1;

const normalizeFieldMappings = (fieldMappings?: MailerLiteFieldMappingData[]) => (
    fieldMappings ?? []
).map(mapping => ({
    mailerLiteFieldKey: mapping.mailerLiteFieldKey,
    mailerLiteFieldName: mapping.mailerLiteFieldName,
    appointmintFieldKey: mapping.appointmintFieldKey ?? null,
    clientTagPrefix: mapping.clientTagPrefix ?? null,
}));

const areMailerLiteSettingsEquivalent = (
    left?: IntegrationMailerLiteSettings,
    right?: IntegrationMailerLiteSettings,
) => (
    (left?.apiKey ?? '') === (right?.apiKey ?? '')
        && JSON.stringify(normalizeFieldMappings(left?.fieldMappings)) === JSON.stringify(normalizeFieldMappings(right?.fieldMappings))
);

export const MailerLiteSettings: FC<IntegrationsSettingsProps> = ({
    values,
    onChange,
    onValidationChange,
}) => {
    const t = useTranslations();
    const dispatch = useDispatch();
    const savedSettings = useSelector(getSettings);

    const mailerLiteSettings: IntegrationMailerLiteSettings = values.mailerLite ?? DEFAULT_SETTINGS;
    const savedMailerLiteSettings: IntegrationMailerLiteSettings = savedSettings.integrations?.mailerLite ?? DEFAULT_SETTINGS;

    const [currentStep, setCurrentStep] = React.useState(getInitialStep(mailerLiteSettings));
    const [isTesting, setIsTesting] = React.useState(false);
    const [isLoadingFields, setIsLoadingFields] = React.useState(false);
    const [isStartingImport, setIsStartingImport] = React.useState(false);
    const [isImportInProgress, setIsImportInProgress] = React.useState(false);
    const [createIfNotExists, setCreateIfNotExists] = React.useState(false);
    const [showTagPrefixHelp, setShowTagPrefixHelp] = React.useState(false);
    const [showImportConfirmation, setShowImportConfirmation] = React.useState(false);
    const [validatedApiKey, setValidatedApiKey] = React.useState(mailerLiteSettings.apiKey);
    const [mappingModesByFieldKey, setMappingModesByFieldKey] = React.useState<Record<string, MappingMode>>({});
    const hasDuplicateMappings = hasDuplicateFieldMappings(mailerLiteSettings.fieldMappings);
    const hasUnsavedChanges = !areMailerLiteSettingsEquivalent(mailerLiteSettings, savedMailerLiteSettings);
    const canStartImport = !hasUnsavedChanges
        && !hasDuplicateMappings
        && !!savedMailerLiteSettings.apiKey
        && !!savedMailerLiteSettings.fieldMappings?.length;

    const getResolvedMappingMode = React.useCallback((mapping: MailerLiteFieldMappingData) => (
        mappingModesByFieldKey[mapping.mailerLiteFieldKey] ?? getMappingMode(mapping)
    ), [mappingModesByFieldKey]);

    React.useEffect(() => {
        const isValid = !!mailerLiteSettings.apiKey
            && validatedApiKey === mailerLiteSettings.apiKey
            && currentStep === 2
            && !hasDuplicateMappings;

        onValidationChange?.(isValid);
    }, [
        currentStep,
        mailerLiteSettings.apiKey,
        mailerLiteSettings.fieldMappings,
        onValidationChange,
        validatedApiKey,
        hasDuplicateMappings,
    ]);

    React.useEffect(() => {
        if (!isImportInProgress || savedMailerLiteSettings.lastSubscribersImportAt) {
            return;
        }

        const intervalId = setInterval(async () => {
            const updatedSettings = await dispatch(loadSettings());
            if (updatedSettings.integrations?.mailerLite?.lastSubscribersImportAt) {
                setIsImportInProgress(false);
                clearInterval(intervalId);
            }
        }, 10000);

        return () => clearInterval(intervalId);
    }, [isImportInProgress, savedMailerLiteSettings.lastSubscribersImportAt, dispatch]);

    const updateMailerLiteSettings = React.useCallback((nextSettings: IntegrationMailerLiteSettings) => {
        onChange({
            ...values,
            mailerLite: nextSettings,
        });
    }, [onChange, values]);

    const handleApiKeyChange = (apiKey: string) => {
        if (apiKey !== validatedApiKey) {
            setCurrentStep(1);
            setMappingModesByFieldKey({});
        }

        updateMailerLiteSettings({ ...mailerLiteSettings, apiKey });
    };

    const handleTestApiKey = async () => {
        setIsTesting(true);

        try {
            await testMailerLiteApiKeyAsync({ apiKey: mailerLiteSettings.apiKey });
            setValidatedApiKey(mailerLiteSettings.apiKey);
            dispatch(alertSuccess(t(TK.mailerLiteApiKeyValidated)));
        } catch (error) {
            setValidatedApiKey('');
            dispatch(alertError(getErrorMessage(error) ?? t(TK.somethingWentWrong)));
        } finally {
            setIsTesting(false);
        }
    };

    const applyLoadedFields = React.useCallback((fields: MailerLiteAvailableFieldData[]) => {
        updateMailerLiteSettings({
            ...mailerLiteSettings,
            fieldMappings: mergeFieldMappings(fields, mailerLiteSettings.fieldMappings),
        });
    }, [mailerLiteSettings, updateMailerLiteSettings]);

    const loadFields = React.useCallback(async () => {
        setIsLoadingFields(true);

        try {
            const response = await getMailerLiteFieldsAsync({ apiKey: mailerLiteSettings.apiKey });
            applyLoadedFields(response.fields);
            return true;
        } catch (error) {
            dispatch(alertError(getErrorMessage(error) ?? t(TK.somethingWentWrong)));
            return false;
        } finally {
            setIsLoadingFields(false);
        }
    }, [applyLoadedFields, dispatch, mailerLiteSettings.apiKey, t]);

    const handleNext = async () => {
        const loaded = await loadFields();
        if (loaded) {
            setCurrentStep(2);
        }
    };

    const updateFieldMapping = (
        mailerLiteFieldKey: string,
        updateMapping: (mapping: MailerLiteFieldMappingData) => MailerLiteFieldMappingData,
    ) => {
        updateMailerLiteSettings({
            ...mailerLiteSettings,
            fieldMappings: (mailerLiteSettings.fieldMappings ?? []).map(mapping =>
                mapping.mailerLiteFieldKey === mailerLiteFieldKey
                    ? updateMapping(mapping)
                    : mapping,
            ),
        });
    };

    const handleMappingChange = (mailerLiteFieldKey: string, appointmintFieldKey: string) => {
        setMappingModesByFieldKey(current => ({ ...current, [mailerLiteFieldKey]: 'field' }));
        updateFieldMapping(mailerLiteFieldKey, mapping => ({
            ...mapping,
            appointmintFieldKey: appointmintFieldKey || undefined,
            clientTagPrefix: undefined,
        }));
    };

    const handleMappingModeChange = (mailerLiteFieldKey: string, mode: MappingMode) => {
        setMappingModesByFieldKey(current => ({ ...current, [mailerLiteFieldKey]: mode }));
        updateFieldMapping(mailerLiteFieldKey, mapping => ({
            ...mapping,
            appointmintFieldKey: mode === 'field' ? mapping.appointmintFieldKey : undefined,
            clientTagPrefix: mode === 'tagPrefix'
                ? (mapping.clientTagPrefix ?? mapping.mailerLiteFieldName?.replace(/\s*:\s*$/, ''))
                : undefined,
        }));
    };

    const handleTagPrefixChange = (mailerLiteFieldKey: string, clientTagPrefix: string) => {
        setMappingModesByFieldKey(current => ({ ...current, [mailerLiteFieldKey]: 'tagPrefix' }));
        updateFieldMapping(mailerLiteFieldKey, mapping => ({
            ...mapping,
            appointmintFieldKey: undefined,
            clientTagPrefix: clientTagPrefix || undefined,
        }));
    };

    const handleStartImport = async () => {
        setIsStartingImport(true);

        try {
            await syncIntegrationAsync(IntegrationType.MailerLite, createIfNotExists);
            dispatch(alertSuccess(t(TK.integrationSyncInitiated)));
            setShowImportConfirmation(false);
            setIsImportInProgress(true);
        } catch (error) {
            dispatch(alertError(getErrorMessage(error) ?? t(TK.somethingWentWrong)));
        } finally {
            setIsStartingImport(false);
        }
    };

    return (
        <Form>
            <Layout>
                <Alert color="secondary" isOpen>
                    {t(TK.mailerLiteIntegrationDescription)}
                </Alert>

                <StepCards>
                {[1, 2].map(step => (
                    <StepCard
                        key={step}
                        $isActive={step === currentStep}
                    >
                        <StepNumber>{t(TK.step)} {step}</StepNumber>
                        <StepTitle>
                            {step === 1 ? t(TK.mailerLiteStepApiKeyTitle) : t(TK.mailerLiteStepMappingTitle)}
                        </StepTitle>
                    </StepCard>
                ))}
                </StepCards>

                {currentStep === 1 ? (
                    <>
                        <TextInputField
                            id="mailerlite-api-key"
                            label={t(TK.mailerLiteApiKeyLabel)}
                            helpText={t(TK.mailerLiteApiKeyHelp)}
                            value={mailerLiteSettings.apiKey}
                            type="password"
                            onChange={handleApiKeyChange}
                        />

                        <ActionsRow>
                            <AppButton
                                id="mailerlite-test-api-key-button"
                                color="secondary"
                                onClick={handleTestApiKey}
                                disabled={!mailerLiteSettings.apiKey || isTesting}
                            >
                                {isTesting ? t(TK.testing) : t(TK.mailerLiteTestApiKey)}
                            </AppButton>
                            <AppButton
                                id="mailerlite-next-button"
                                color="success"
                                onClick={handleNext}
                                disabled={!mailerLiteSettings.apiKey || validatedApiKey !== mailerLiteSettings.apiKey || isLoadingFields}
                            >
                                {isLoadingFields ? t(TK.loading) : t(TK.next)}
                            </AppButton>
                        </ActionsRow>
                    </>
                ) : (
                    <>
                        <MappingHeader>
                            <HintText>{t(TK.mailerLiteFieldMappingHint)}</HintText>
                            <MappingActions>
                                <AppButton color="secondary" onClick={() => setCurrentStep(1)}>
                                    {t(TK.back)}
                                </AppButton>
                                <AppButton
                                    id="mailerlite-refresh-fields-button"
                                    color="secondary"
                                    onClick={loadFields}
                                    disabled={isLoadingFields}
                                >
                                    {isLoadingFields ? t(TK.loading) : t(TK.refresh)}
                                </AppButton>
                            </MappingActions>
                        </MappingHeader>

                        {hasDuplicateMappings ? (
                            <Alert color="danger" isOpen>
                                {t(TK.mailerLiteDuplicateMappingError)}
                            </Alert>
                        ) : null}

                        {(mailerLiteSettings.fieldMappings ?? []).length === 0 ? (
                            <Alert color="warning" isOpen>
                                {t(TK.mailerLiteNoFieldsFound)}
                            </Alert>
                        ) : (
                            <StyledTable responsive scrollable>
                                <thead>
                                    <tr>
                                        <th>{t(TK.mailerLiteTableMailerLiteField)}</th>
                                        <th>{t(TK.mailerLiteTableMappingType)}</th>
                                        <th>{t(TK.mailerLiteTableAppointmintField)}</th>
                                    </tr>
                                </thead>
                                <MappingTableBody>
                                    {(mailerLiteSettings.fieldMappings ?? []).map(mapping => (
                                        <tr key={mapping.mailerLiteFieldKey}>
                                            <td>
                                                <PrimaryText>{mapping.mailerLiteFieldName}</PrimaryText>
                                                <FieldKeyText>{mapping.mailerLiteFieldKey}</FieldKeyText>
                                            </td>
                                            <td>
                                                <TextField
                                                    id={`mailerlite-mapping-mode-${mapping.mailerLiteFieldKey}`}
                                                    select
                                                    size="small"
                                                    fullWidth
                                                    value={getResolvedMappingMode(mapping)}
                                                    label={getResolvedMappingMode(mapping) === 'tagPrefix'
                                                        ? <span>{t(TK.mailerLiteTableMappingType)} <IconButton size="small" onClick={e => { e.stopPropagation(); setShowTagPrefixHelp(true); }}><HelpIcon style={{ fontSize: '1rem' }} /></IconButton></span>
                                                        : t(TK.mailerLiteTableMappingType)}
                                                    onChange={event => handleMappingModeChange(mapping.mailerLiteFieldKey, event.target.value as MappingMode)}
                                                >
                                                    <MenuItem value="none">{t(TK.mailerLiteMappingTypeNone)}</MenuItem>
                                                    <MenuItem value="field">{t(TK.mailerLiteMappingTypeProfileField)}</MenuItem>
                                                    <MenuItem value="tagPrefix">{t(TK.mailerLiteMappingTypeClientTagPrefix)}</MenuItem>
                                                </TextField>
                                            </td>
                                            <td>
                                                {getResolvedMappingMode(mapping) === 'field' ? (
                                                    <TextField
                                                        id={`mailerlite-mapping-field-${mapping.mailerLiteFieldKey}`}
                                                        select
                                                        size="small"
                                                        fullWidth
                                                        value={mapping.appointmintFieldKey ?? ''}
                                                        label={t(TK.mailerLiteTableAppointmintField)}
                                                        onChange={event => handleMappingChange(mapping.mailerLiteFieldKey, event.target.value)}
                                                    >
                                                        <MenuItem value="">{t(TK.mailerLiteNoMappingOption)}</MenuItem>
                                                        {APPOINTMINT_FIELD_OPTIONS.map(option => (
                                                            <MenuItem key={option.key} value={option.key}>
                                                                {t(option.labelKey)}
                                                            </MenuItem>
                                                        ))}
                                                    </TextField>
                                                ) : null}
                                                {getResolvedMappingMode(mapping) === 'tagPrefix' ? (
                                                    <TextInputField
                                                        id={`mailerlite-tag-prefix-${mapping.mailerLiteFieldKey}`}
                                                        label={t(TK.mailerLiteTagPrefixLabel)}
                                                        description={t(TK.mailerLiteTagPrefixHelp)}
                                                        value={mapping.clientTagPrefix ?? ''}
                                                        onChange={value => handleTagPrefixChange(mapping.mailerLiteFieldKey, value)}
                                                    />
                                                ) : null}
                                                {getResolvedMappingMode(mapping) === 'none' ? (
                                                    <MutedText>{t(TK.mailerLiteNoMappingOption)}</MutedText>
                                                ) : null}
                                            </td>
                                        </tr>
                                    ))}
                                </MappingTableBody>
                            </StyledTable>
                        )}

                        <ImportCard>
                            <ImportHeader>
                                <div>
                                    <ImportTitle>{t(TK.mailerLiteImportSectionTitle)}</ImportTitle>
                                    <StatusText>{t(TK.mailerLiteImportDescription)}</StatusText>
                                </div>
                                <AppButton
                                    id="mailerlite-import-button"
                                    color="success"
                                    onClick={() => setShowImportConfirmation(true)}
                                    disabled={!canStartImport || isStartingImport}
                                >
                                    {isStartingImport ? t(TK.syncing) : t(TK.mailerLiteImportButton)}
                                </AppButton>
                            </ImportHeader>
                            <CheckInputField
                                id="mailerlite-create-if-not-exists"
                                label={t(TK.mailerLiteImportCreateIfNotExists)}
                                helpText={t(TK.mailerLiteImportCreateIfNotExistsHelp)}
                                value={createIfNotExists}
                                onChange={setCreateIfNotExists}
                                disabled={!canStartImport}
                            />

                            {isImportInProgress && !savedMailerLiteSettings.lastSubscribersImportAt ? (
                                <ImportProgressRow>
                                    <CircularProgress size={16} />
                                    <StatusText>{t(TK.mailerLiteImportInProgress)}</StatusText>
                                </ImportProgressRow>
                            ) : null}

                            {savedMailerLiteSettings.lastSubscribersImportAt ? (
                                <StatusText>
                                    {t(TK.mailerLiteImportCompletedAt)} {moment(savedMailerLiteSettings.lastSubscribersImportAt).locale(getLanguage()).format('LLL')}
                                </StatusText>
                            ) : null}

                            {hasUnsavedChanges ? (
                                <StatusText>{t(TK.mailerLiteImportSaveChangesFirst)}</StatusText>
                            ) : null}
                        </ImportCard>
                    </>
                )}
            </Layout>

            <Modal isOpen={showImportConfirmation} toggle={() => setShowImportConfirmation(false)}>
                <ModalHeader toggle={() => setShowImportConfirmation(false)}>
                    {t(TK.mailerLiteImportConfirmationTitle)}
                </ModalHeader>
                <ModalBody>
                    {t(TK.mailerLiteImportConfirmationMessage)}
                </ModalBody>
                <ModalFooter>
                    <AppButton
                        id="mailerlite-import-confirm-button"
                        color="primary"
                        onClick={handleStartImport}
                        disabled={isStartingImport}
                    >
                        {isStartingImport ? t(TK.syncing) : t(TK.continueButton)}
                    </AppButton>
                    <AppButton
                        id="mailerlite-import-cancel-button"
                        color="secondary"
                        onClick={() => setShowImportConfirmation(false)}
                        disabled={isStartingImport}
                    >
                        {t(TK.cancel)}
                    </AppButton>
                </ModalFooter>
            </Modal>

            <Dialog open={showTagPrefixHelp} onClose={() => setShowTagPrefixHelp(false)}>
                <DialogTitle>{t(TK.mailerLiteMappingTypeClientTagPrefix)}</DialogTitle>
                <DialogContent>
                    {t(TK.mailerLiteTagPrefixHelpText)
                        .split('\n')
                        .map(line => <div key={line}>{line}</div>)}
                </DialogContent>
            </Dialog>
        </Form>
    );
};
